import { useEffect, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { diccionarioDe, type Idioma } from '../../../i18n/index.js';
import { useSesion } from '../../../common/hooks/useSesion.js';
import { useNoIndex } from '../../../common/hooks/useNoIndex.js';
import { decidirAutorizacion, detallesDeAutorizacion, type DetallesDeAutorizacion } from '../../../common/services/auth.js';

/** La navegación a otra web, aparte para poder comprobarla en los tests sin salir de la página. */
export const irA = (url: string): void => {
  window.location.assign(url);
};

/** Solo el dominio de una dirección, para enseñarlo claro; la dirección entera si no se puede leer. */
/** Si la aplicación vuelve al propio ordenador (Claude Code, un cliente de escritorio): 127.0.0.1 o localhost. */
const esLocal = (url: string): boolean => {
  try {
    return ['localhost', '127.0.0.1', '[::1]'].includes(new URL(url).hostname);
  } catch {
    return false;
  }
};

const dominioDe = (url: string): string => {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
};

/**
 * MCP-01: la pantalla de consentimiento del OAuth Server de Supabase (`/oauth/consent`, la ruta
 * configurada en Supabase). Llega aquí quien conecta Athena desde ChatGPT, Claude u otro asistente.
 *
 * Lo que tiene que quedar claro, para una organizadora que no sabe qué es OAuth: **quién** pide
 * acceso (nombre de la aplicación) y **adónde** vuelve (el dominio, porque un nombre se puede
 * falsear y el dominio no), **qué** podrá hacer (consultar Athena con su cuenta, solo leer) y dos
 * botones claros. Sin sesión, primero entrar (enlace mágico) y volver aquí.
 */
export function ConsentimientoOAuthPage({ idioma }: { readonly idioma: Idioma }) {
  const t = diccionarioDe(idioma).cuentas.consentimiento;
  useNoIndex(true);
  const sesion = useSesion();
  const [params] = useSearchParams();
  const id = params.get('authorization_id');
  const [detalles, setDetalles] = useState<DetallesDeAutorizacion | null | undefined>(undefined);
  const [decidiendo, setDecidiendo] = useState(false);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    if (id === null || !sesion) return;
    let vigente = true;
    void detallesDeAutorizacion(id).then(d => {
      if (!vigente) return;
      // Ya lo autorizó antes: de vuelta a la aplicación sin preguntar otra vez.
      if (d?.tipo === 'yaConcedida') irA(d.irA);
      else setDetalles(d);
    });
    return () => {
      vigente = false;
    };
  }, [id, sesion]);

  const decidir = (permitir: boolean) => {
    if (id === null) return;
    setDecidiendo(true);
    setFallo(false);
    void decidirAutorizacion(id, permitir).then(url => {
      if (url === null) {
        setDecidiendo(false);
        setFallo(true);
        return;
      }
      irA(url);
    });
  };

  const marco = (hijos: ReactNode) => <main className='mx-auto flex w-full max-w-[480px] grow flex-col gap-5 px-6 py-16'>{hijos}</main>;
  const aviso = (texto: string) => marco(<p className='m-0 font-(family-name:--text) text-[16px] text-(--ink-soft)'>{texto}</p>);

  if (id === null) return aviso(t.sinSolicitud);
  if (sesion === undefined) return aviso(t.cargando);
  if (sesion === null) {
    const volver = `/oauth/consent?authorization_id=${encodeURIComponent(id)}`;
    return marco(
      <>
        <h1 className='m-0 font-(family-name:--serif) text-[26px] leading-[1.2] font-bold text-(--ink)'>{t.entraTitulo}</h1>
        <p className='m-0 font-(family-name:--text) text-[15px] text-(--ink-soft)'>{t.entraTexto}</p>
        <Link
          to={`/iniciar-sesion?volver=${encodeURIComponent(volver)}`}
          className='sobre-teal w-fit rounded-full bg-(--teal-deep) px-5 py-3 font-(family-name:--text) text-[15px] no-underline'
        >
          {t.entrar}
        </Link>
      </>,
    );
  }
  if (detalles === undefined) return aviso(t.cargando);
  if (detalles === null || detalles.tipo !== 'pedir') return aviso(t.caducada);

  return marco(
    <>
      <h1 className='m-0 font-(family-name:--serif) text-[26px] leading-[1.2] font-bold text-(--ink)'>
        {t.titulo.replace('{app}', detalles.cliente.nombre)}
      </h1>
      <p className='m-0 font-(family-name:--text) text-[15px] text-(--ink-soft)'>{t.cuenta.replace('{correo}', detalles.correo)}</p>

      <dl className='m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 rounded-[3px] border border-(--line-strong) bg-(--chat-bg) p-4'>
        <dt className='font-(family-name:--text) text-[13.5px] text-(--ink-faint)'>{t.quien}</dt>
        <dd className='m-0 font-(family-name:--text) text-[15px] font-semibold text-(--ink)'>{detalles.cliente.nombre}</dd>
        <dt className='font-(family-name:--text) text-[13.5px] text-(--ink-faint)'>{t.vuelve}</dt>
        <dd className='m-0 font-(family-name:--mono) text-[13.5px] text-(--ink) [overflow-wrap:anywhere]'>
          {dominioDe(detalles.vuelveA)}
          {/* Una dirección local asusta a quien no sabe qué es: se explica en una línea. */}
          {esLocal(detalles.vuelveA) ? <span className='mt-1 block font-(family-name:--text) text-[13px] text-(--ink-soft)'>{t.local}</span> : null}
        </dd>
      </dl>

      <div>
        <p className='m-0 mb-2 font-(family-name:--text) text-[14px] text-(--ink-faint)'>{t.podra}</p>
        <ul className='m-0 flex list-disc flex-col gap-1 pl-5 font-(family-name:--text) text-[15px] text-(--ink)'>
          <li>{t.consultar}</li>
          <li>{t.soloLeer}</li>
        </ul>
      </div>

      <p className='m-0 font-(family-name:--text) text-[14px] text-(--ink-soft)'>{t.siNoReconoces}</p>

      <div className='flex flex-wrap gap-3'>
        <button
          type='button'
          disabled={decidiendo}
          onClick={() => {
            decidir(true);
          }}
          className='sobre-teal cursor-pointer rounded-full bg-(--teal-deep) px-6 py-3 font-(family-name:--text) text-[15px] disabled:cursor-not-allowed disabled:opacity-50'
        >
          {t.permitir}
        </button>
        <button
          type='button'
          disabled={decidiendo}
          onClick={() => {
            decidir(false);
          }}
          className='cursor-pointer rounded-full border border-(--line-strong) bg-transparent px-6 py-3 font-(family-name:--text) text-[15px] text-(--ink) disabled:cursor-not-allowed disabled:opacity-50'
        >
          {t.noPermitir}
        </button>
      </div>

      {fallo ? (
        <p role='alert' className='m-0 font-(family-name:--text) text-[14px] text-(--ink-soft)'>
          {t.fallo}
        </p>
      ) : null}
      <p className='m-0 font-(family-name:--text) text-[13px] text-(--ink-faint)'>{t.retirar}</p>
    </>,
  );
}
